import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import logoImg from "@/assets/images/logo.png";
import { useAuth } from "@/hooks/use-auth";
import { InstallApkDialog } from "@/components/InstallApkDialog";
import { useEffect, useState } from "react";
import {
  FileText,
  Shield,
  Smartphone,
  Monitor,
  Users,
  Package,
  Boxes,
  CheckCircle,
  ArrowRight,
  Download,
  ClipboardCheck,
  Camera,
  Building2,
} from "lucide-react";

const features = [
  {
    icon: ClipboardCheck,
    title: "Laudos Técnicos Digitais",
    description:
      "Crie, gerencie e assine laudos técnicos de vistoria veicular diretamente no celular ou computador. Conforme Lei 12.977/14.",
  },
  {
    icon: Camera,
    title: "Registro Fotográfico",
    description:
      "Capture e anexe fotos das peças e do veículo diretamente no laudo, garantindo documentação completa da vistoria.",
  },
  {
    icon: Package,
    title: "Catálogo de Peças",
    description:
      "Base completa de peças por tipo de veículo (auto leve, moto, caminhão, ônibus) com estados de conservação por UF.",
  },
  {
    icon: Users,
    title: "Gestão Multi-Equipe",
    description:
      "Controle técnicos de vistoria, responsáveis técnicos e usuários com permissões específicas para cada papel.",
  },
  {
    icon: Building2,
    title: "Multi-Empresa",
    description: "Gerência de empresas em um único sistema integrado.",
  },
  {
    icon: Boxes,
    title: "Lotes Mensais",
    description:
      "Agrupe laudos finalizados por mês para controle de produção e faturamento organizado.",
  },
];

const appFeatures = [
  "Criar e enviar laudos de vistoria no campo",
  "Fotografar peças e veículos com o celular",
  "Preencher respostas e destinações offline",
  "Assinar digitalmente os laudos",
  "Receber notificações de novos laudos",
];

const webFeatures = [
  "Painel administrativo completo",
  "Gerenciamento empresarial",
  "Visualizar e acompanhar todos os laudos",
  "Controle técnico de equipes",
  "Relatórios e lotes mensais",
];

export default function LandingPage() {
  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [installDialogOpen, setInstallDialogOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      setLocation("/dashboard");
    }
  }, [authLoading, user, setLocation]);

  return (
    <>
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logoImg}
              alt="LaudoLegal"
              className="h-10 w-10 object-contain"
            />
            <span className="font-display font-bold text-xl text-slate-900">
              LaudoLegal
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/login">
              <Button variant="ghost" data-testid="link-login">
                Entrar
              </Button>
            </Link>
            <Link href="/registrar">
              <Button variant="outline" data-testid="link-registrar-nav">
                Registrar
              </Button>
            </Link>
            {/* <a href="#download">
              <Button
                className="shadow-lg shadow-primary/25"
                data-testid="button-download-nav"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar App
              </Button>
            </a> */}
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-8">
              <img
                src={logoImg}
                alt="LaudoLegal Logo"
                className="h-32 w-32 object-contain drop-shadow-xl"
                data-testid="img-logo-hero"
              />
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-slate-900 leading-tight">
              Laudo Técnico Veicular
              <span className="block text-primary mt-2">Rápido e Fácil</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Plataforma completa para vistoria veicular. Gerencie laudos
              técnicos, peças, equipes e parceiros de negócios em conformidade
              com a{"\n"}
              <strong>Lei 12.977/14</strong>.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              {/* <a href="#download">
                <Button
                  size="lg"
                  className="shadow-xl shadow-primary/30 gap-2"
                  data-testid="button-hero-download"
                >
                  <Download className="w-5 h-5" />
                  Baixar Aplicativo
                </Button>
              </a> */}
              <Link href="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2"
                  data-testid="button-hero-login"
                >
                  Acessar Painel Web
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 bg-slate-50" id="funcionalidades">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-2">
              Funcionalidades
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">
              Tudo que você precisa em um só lugar
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Do campo ao escritório, o LaudoLegal conecta toda a cadeia de
              vistoria veicular.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, i) => (
              <Card
                key={i}
                className="border-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
                data-testid={`card-feature-${i}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-2">
              App + Web
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">
              Dois ambientes, uma plataforma
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              O app mobile para o trabalho em campo e o painel web para a gestão
              administrativa.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            <Card className="border-none shadow-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-8 relative">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                  <Smartphone className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-2xl mb-2">
                  App Mobile
                </h3>
                <p className="text-blue-100 mb-6">
                  Para técnicos de vistoria, responsáveis técnicos e parceiros
                  de negócio atuarem no campo.
                </p>
                <ul className="space-y-3">
                  {appFeatures.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-200 flex-shrink-0" />
                      <span className="text-blue-50">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-200" />
                  <span className="text-xs text-blue-200">
                    Android {/* e iOS */}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-slate-800 to-slate-900 text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <CardContent className="p-8 relative">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                  <Monitor className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-2xl mb-2">
                  Painel Web
                </h3>
                <p className="text-slate-300 mb-6">
                  Para administradores e gestores controlarem toda a operação.
                </p>
                <ul className="space-y-3">
                  {webFeatures.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-200">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link href="/login">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-white/20 text-white bg-transparent gap-2"
                      data-testid="button-painel-login"
                    >
                      Acessar Painel
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 bg-slate-50" id="quem-usa">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-2">
              Para quem
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">
              Quem usa o LaudoLegal?
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                role: "Vistoriadoras",
                desc: "Realizam os laudos técnicos e gerenciam equipes de campo.",
                icon: Building2,
                color: "bg-blue-100 text-blue-700",
              },
              {
                role: "Técnicos de Vistoria",
                desc: "Profissionais que vão a campo, inspecionam veículos e preenchem laudos.",
                icon: ClipboardCheck,
                color: "bg-amber-100 text-amber-700",
              },
              {
                role: "Responsáveis Técnicos",
                desc: "Engenheiros que revisam, avaliam e assinam os laudos finalizados.",
                icon: Shield,
                color: "bg-purple-100 text-purple-700",
              },
              {
                role: "Parceiros de negócios",
                desc: "Quem solicitam vistorias para seus veículos.",
                icon: Users,
                color: "bg-emerald-100 text-emerald-700",
              },
            ].map((item, i) => (
              <Card
                key={i}
                className="border-none shadow-md text-center bg-white"
                data-testid={`card-role-${i}`}
              >
                <CardContent className="p-6">
                  <div
                    className={`w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center ${item.color}`}
                  >
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-display font-bold text-base text-slate-900 mb-2">
                    {item.role}
                  </h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6" id="download">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <img
              src={logoImg}
              alt="LaudoLegal"
              className="h-20 w-20 object-contain"
            />
          </div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-slate-900 mb-4">
            Baixe o LaudoLegal agora
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-10">
            Disponível para Android. Comece a emitir laudos digitais hoje
            mesmo.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setInstallDialogOpen(true);
              }}
              className="flex items-center gap-3 rounded-xl bg-slate-900 text-white py-4 px-6 min-w-[220px] shadow-lg transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
              data-testid="button-download-android"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-7 h-7 fill-current flex-shrink-0"
              >
                <path d="M17.523 2.237a.625.625 0 0 0-.87.168l-1.79 2.755A8.97 8.97 0 0 0 12 4.5a8.97 8.97 0 0 0-2.863.66L7.347 2.405a.625.625 0 1 0-1.038.698l1.712 2.636A9.036 9.036 0 0 0 3 13h18a9.036 9.036 0 0 0-5.021-7.261l1.712-2.636a.625.625 0 0 0 .168-.866zM8.5 10.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm7 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM3 14v6a2 2 0 0 0 2 2h1V14H3zm2 9h4v-9H5v9zm5 0h4v-9h-4v9zm5 0h4v-9h-4v9zm5-9v6a2 2 0 0 1-2 2h-1V14h3z" />
              </svg>
              <div className="text-left">
                <p className="text-xs text-slate-400">Baixar para</p>
                <p className="font-bold text-lg leading-tight">Android</p>
              </div>
            </a>
          </div>

          <p className="text-xs text-slate-400 mt-6">
            Versão iOS em breve.
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold mb-4">
            Pronto para modernizar suas vistorias?
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto mb-8">
            Deixe o papel para trás. Emita laudos digitais com validade legal,
            controle sua equipe e seus parceiros de negócios em uma plataforma
            segura.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* <a href="#download">
              <Button
                size="lg"
                variant="secondary"
                className="text-primary font-bold gap-2"
                data-testid="button-cta-download"
              >
                <Download className="w-5 h-5" />
                Baixar o App
              </Button>
            </a> */}
            <Link href="/login">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white bg-transparent gap-2"
                data-testid="button-cta-login"
              >
                Acessar Painel Web
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10 px-4 sm:px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src={logoImg}
              alt="LaudoLegal"
              className="h-8 w-8 object-contain"
            />
            <span className="font-display font-bold">LaudoLegal</span>
            <span className="text-slate-500 text-sm">|</span>
            <span className="text-slate-400 text-sm">Laudo Rápido e Fácil</span>
          </div>
          <p className="text-slate-500 text-sm">
            Lei 12.977/14 - Desmontagem de veículos
          </p>
        </div>
      </footer>
    </div>
    <InstallApkDialog open={installDialogOpen} onOpenChange={setInstallDialogOpen} />
    </>
  );
}
